import Loader from "_shared/_views/loading/loader";
import Box from "@mui/material/Box";
import {
  getPermissionsViewMode,
  parsePermissionsRouteState,
} from "permissions/_views/tables/_utilities/permissions-route-state";
import { PermissionProps } from "permissions/_views/permission-table-view-model";
import AdministrationPermissionsHeader from "permissions/_views/tables/administration-permissions-header";
import { lazy, Suspense } from "react";
import { useLocation } from "react-router";

const AdditionalPermissionsTable = lazy(() => import("permissions/_views/tables/additional-permissions-table"));
const CreateTemplatePage = lazy(() => import("permissions/_views/tables/create-template-page"));
const PermissionsHome = lazy(() => import("permissions/_views/tables/permissions-home"));
const PermissionsMatrixTable = lazy(() => import("permissions/_views/tables/permissions-matrix-table"));
const UserPermissionsOverview = lazy(() => import("permissions/_views/tables/user-permissions-overview"));

function PermissionsLoadingState(props: Readonly<{ text?: string }>) {
  const text = props.text ?? "Loading";
  return (
    <Box sx={{ alignItems: "center", display: "flex", flex: 1, height: "100%", justifyContent: "center", minHeight: 0, p: 2 }}>
      <Loader text={text} />
    </Box>
  );
}

export function shouldShowAdditionalPermissionsTable(search: string): boolean {
  const viewMode = getPermissionsViewMode(search);
  return viewMode === "details" || (viewMode === "matrix" && parsePermissionsRouteState(search).hasParent === true);
}

/**
 * Chooses the smallest permissions surface that can explain the current URL.
 * The resource matrix is intentionally mounted only for an explicit person,
 * templates, or an existing filter/deep-link workflow.
 */
export default function PermissionsTable(props: Readonly<PermissionProps>) {
  const location = useLocation();
  const viewMode = getPermissionsViewMode(location.search);

  if (viewMode === "grantee") {
    return (
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
        <AdministrationPermissionsHeader />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Suspense fallback={<PermissionsLoadingState text="Loading user permissions" />}>
            <UserPermissionsOverview />
          </Suspense>
        </Box>
      </Box>
    );
  }

  if (viewMode === "create-template") {
    return (
      <Suspense fallback={<PermissionsLoadingState />}>
        <CreateTemplatePage />
      </Suspense>
    );
  }

  if (viewMode === "home") {
    return (
      <Suspense fallback={<PermissionsLoadingState />}>
        <PermissionsHome />
      </Suspense>
    );
  }

  return (
    <>
      <Suspense fallback={<PermissionsLoadingState />}>
        {shouldShowAdditionalPermissionsTable(location.search) ? (
          <AdditionalPermissionsTable {...props} />
        ) : (
          <PermissionsMatrixTable {...props} />
        )}
      </Suspense>
    </>
  );
}
